import React, { useCallback, useEffect, useState } from 'react';
import { adminAPI } from '../../../services/authService';
import { confirmDialog } from '../../../utils/confirmDialog';
import { notifyError, notifySuccess } from '../../../utils/notify';

const UsersAccessInvitations = ({ initialStatus = '' }) => {
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [status, setStatus] = useState(['FAILED', 'EXPIRED', 'SENT', 'SETUP_PENDING', 'ACTIVATED', 'CANCELLED'].includes(String(initialStatus || '').toUpperCase()) ? String(initialStatus).toUpperCase() : '');
  const [userId, setUserId] = useState('');
  const [preview, setPreview] = useState(null);

  const load = useCallback(async () => {
    const [inv, u] = await Promise.all([
      adminAPI.getInvitations(status ? { status } : {}),
      adminAPI.getUsers(),
    ]);
    setRows(inv?.data?.data || inv?.data || []);
    setUsers(u?.data?.data || u?.data || []);
  }, [status]);

  useEffect(() => {
    const next = String(initialStatus || '').toUpperCase();
    if (['FAILED', 'EXPIRED', 'SENT', 'SETUP_PENDING', 'ACTIVATED', 'CANCELLED'].includes(next)) {
      setStatus(next);
    }
  }, [initialStatus]);

  useEffect(() => { load().catch(() => notifyError('Unable to load invitations')); }, [load]);

  const sendInvite = async (id, resend = true) => {
    try {
      const res = await adminAPI.inviteUser(id, { resend, invalidatePrevious: true });
      if (res?.data?.invitationFailed) notifyError(res.data.reason || 'Email failed');
      else notifySuccess(resend ? 'Invitation resent' : 'Invitation sent');
      await load();
    } catch (err) {
      notifyError(err?.response?.data?.message || 'Invite failed');
    }
  };

  const cancel = async (id) => {
    if (!(await confirmDialog('Cancel this invitation?', 'Cancel Invitation'))) return;
    try {
      await adminAPI.cancelInvitation(id);
      notifySuccess('Cancelled');
      await load();
    } catch (err) {
      notifyError(err?.response?.data?.message || 'Cancel failed');
    }
  };

  const showPreview = async (uid) => {
    try {
      const res = await adminAPI.getInvitePreview(uid);
      setPreview(res?.data);
    } catch (err) {
      notifyError(err?.response?.data?.message || 'Preview failed');
    }
  };

  return (
    <div>
      <div className="filter-bar">
        <select value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All statuses</option>
          <option value="SENT">Sent</option>
          <option value="SETUP_PENDING">Setup Pending</option>
          <option value="ACTIVATED">Activated</option>
          <option value="EXPIRED">Expired</option>
          <option value="FAILED">Failed</option>
          <option value="CANCELLED">Cancelled</option>
        </select>
        <select value={userId} onChange={(e) => setUserId(e.target.value)}>
          <option value="">Send to user…</option>
          {users.map((u) => <option key={u.id} value={u.id}>{u.name} ({u.email})</option>)}
        </select>
        <button type="button" className="btn-primary" disabled={!userId} onClick={() => sendInvite(Number(userId), false)}>Send Invitation</button>
        <button type="button" className="btn-secondary" disabled={!userId} onClick={() => showPreview(Number(userId))}>Preview</button>
      </div>

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Recipient</th><th>Email</th><th>Account Type</th><th>Roles</th><th>Projects</th>
              <th>Status</th><th>Sent At</th><th>Expires At</th><th>Attempts</th><th>Last Error</th><th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={11} style={{ textAlign: 'center', color: '#64748b', padding: 20 }}>No invitations</td></tr>
            ) : rows.map((r) => (
              <tr key={r.id}>
                <td>{r.recipientName || r.user?.name}</td>
                <td>{r.recipientEmail || r.user?.email}</td>
                <td>{r.accountType || '—'}</td>
                <td>{(r.systemRoles || []).join(', ') || '—'}</td>
                <td>{(r.projects || []).join(', ') || '—'}</td>
                <td><span className={`ua-badge ${r.status === 'FAILED' ? 'ua-badge--danger' : r.status === 'ACTIVATED' ? 'ua-badge--ok' : 'ua-badge--warn'}`}>{r.status}</span></td>
                <td>{r.sentAt ? new Date(r.sentAt).toLocaleString('en-IN') : '—'}</td>
                <td>{r.expiresAt ? new Date(r.expiresAt).toLocaleString('en-IN') : '—'}</td>
                <td>{r.attemptNumber}</td>
                <td style={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.lastError || '—'}</td>
                <td style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                  <button type="button" className="btn-secondary btn-sm" onClick={() => sendInvite(r.userId, true)}>Resend</button>
                  <button type="button" className="btn-secondary btn-sm" onClick={() => showPreview(r.userId)}>Preview</button>
                  {r.status !== 'ACTIVATED' && r.status !== 'CANCELLED' && (
                    <button type="button" className="btn-secondary btn-sm" onClick={() => cancel(r.id)}>Cancel</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {preview && (
        <div className="ua-drawer-overlay" onClick={() => setPreview(null)}>
          <div className="ua-drawer" onClick={(e) => e.stopPropagation()} style={{ width: 'min(720px, 100%)' }}>
            <h2 style={{ marginTop: 0 }}>Invitation email preview</h2>
            <p style={{ fontSize: 13 }}><strong>Subject:</strong> {preview.subject}</p>
            <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }} dangerouslySetInnerHTML={{ __html: preview.html }} />
            <button type="button" className="btn-secondary" style={{ marginTop: 12 }} onClick={() => setPreview(null)}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersAccessInvitations;
